import { pb } from './pb';
import type { Round as PlayRound } from '../data/types';
import type { HeadlineSize } from '../play/headlineScale';

/* Client for /api/rof/*.

   Every call carries the caller's own PocketBase token. The server re-resolves who they are
   from it — the browser is never trusted for identity, brand or attribution, so this file
   deliberately has no way to send any of those. */

const BASE = '/api/rof';

export interface ApiFailure {
  error: string;
  message: string;
  status: number;
}

export class ApiCallError extends Error {
  status: number;
  code: string;
  /* The whole parsed body. A 409 from a stale write carries the version that beat you and who
     saved it, and dropping that would leave the caller knowing it failed but not what to show. */
  details: unknown;
  constructor(failure: ApiFailure, details: unknown = null) {
    super(failure.message);
    this.status = failure.status;
    this.code = failure.error;
    this.details = details;
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const token = pb.authStore.token;
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json');

  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    // A JSON endpoint returning HTML means the request never reached the Function — usually a
    // routing problem. Say that, rather than "Unexpected token '<'".
    throw new ApiCallError({
      error: 'bad_response',
      message: 'The server returned an unexpected response. Reload and try again.',
      status: res.status,
    });
  }

  if (!res.ok) {
    const failure = body as Partial<ApiFailure> | null;
    throw new ApiCallError({
      error: failure?.error || 'request_failed',
      message: failure?.message || 'Something went wrong.',
      status: res.status,
    }, body);
  }
  return body as T;
}

// ── types ───────────────────────────────────────────────────────────────────────────────────

export type Verdict = 'real' | 'fake';
export type RoundStatus = 'draft' | 'ready' | 'recorded' | 'archived';
/** Endless Mode ordering. Sticky — it stays as set until someone changes it. */
export type PlayOrder = 'queue' | 'shuffled';
export type RoundKind = 'series' | 'endless' | 'intake';

export interface ItemImage {
  name: string;
  url: string;
}

export interface RoundItem {
  id: string;
  /** Readable id used by preview links. Assigned once, then stable. */
  slug: string;
  roundId: string;
  position: number;
  headline: string;
  verdict: Verdict;
  sourceName: string;
  sourceUrl: string;
  /* When the article was published: `YYYY` or `YYYY-MM-DD`, whichever is actually known.
     Empty when nobody has recorded one. Builder-side only — never on the play surface. */
  publishedAt: string;
  /** '' | 'needs-art' | 'ready' — whether the fake page's artwork is actually done. */
  artStatus: string;
  images: ItemImage[];
  body: string;
  footerNote: string;
  imageHint: string;
  notes: string;
  /** When it landed in our database. Distinct from publishedAt, which is about the article. */
  createdAt: string;
  updated: string;
  /** Empty on everything the builder shows. Only the trash reads it. */
  deletedAt?: string;
}

export interface RoundCounts {
  total: number;
  real: number;
  fake: number;
  missing: number;
  /** Fakes explicitly waiting on a designer. Counted apart from `missing` — different job. */
  needsArt: number;
}

export interface RoundSummary {
  id: string;
  title: string;
  status: RoundStatus;
  targetSize: number | null;
  notes: string;
  /** The corner banner is a round setting — every fake page in the round agrees. */
  ribbonEnabled: boolean;
  ribbonLabel: string;
  /** Headline size for the whole round; long headlines still step down automatically. */
  headlineSize: HeadlineSize;
  /** Show the "Adorian" wordmark on this round's fake pages. */
  wordmarkEnabled: boolean;
  /* Which pool this row is. Set once at creation and never patchable — moving a series into
     the endless pool would silently relocate every headline under it. */
  kind: RoundKind;
  /** Endless Mode only. Ignored on a series, which always plays in the order it was built. */
  playOrder: PlayOrder;
  stampRealUrl: string | null;
  stampFakeUrl: string | null;
  created: string;
  updated: string;
  counts: RoundCounts;
}

export interface RoundDetail extends Omit<RoundSummary, 'counts'> {
  items: RoundItem[];
}

/* Headlines per decade — the check behind "spanning roughly the last few decades". Undated
   items are counted apart rather than dropped: a pool that is mostly undated is a different
   problem from one that is mostly recent, and folding them together would hide it. */
/* One headline that is not ready, and why. Objects rather than sentences so the UI can group
   them by kind and link to the headline — see readinessProblems in functions/_lib/roundsApi.js. */
export interface ReadinessProblem {
  id: string;
  /** Position in the round, 0-based. Displayed as index + 1. */
  index: number;
  kind: 'no-text' | 'no-link' | 'needs-art' | 'placeholder-art' | 'empty-fake';
  /** The full sentence, kept for the tooltip. */
  message: string;
}

export interface DecadeSpread {
  decades: { decade: number; count: number }[];
  undated: number;
  dated: number;
}

/* When the Intake Queue was last filled.
 *
 * `lastPulledAt` is when a collector last RAN — not when a candidate last arrived. A run that
 * finds nothing new still counts, which is the whole point: otherwise a quiet week and a dead
 * collector are the same number on screen. `ok: null` means that collector has never run.
 *
 * The whole object is `null` when the job ledger could not be read; the line then renders as
 * nothing rather than guessing. */
export interface CollectorRuns {
  lastPulledAt: string;
  ok: boolean | null;
  runs: { job: string; label: string; cadence: string; at: string; ok: boolean | null }[];
}

export interface ShareLink {
  id: string;
  token: string;
  roundId: string;
  label: string;
  revoked: boolean;
  expiresAt: string | null;
  viewCount: number;
  lastViewedAt: string | null;
  created: string;
}

/** One link's verdict from the Shoot-Ready check. `verdict` empty means it opens fine. */
export interface PreflightResult {
  id: string;
  verdict: string;
}

/** One recorded play of a round. */
export interface PlaySession {
  id: string;
  /** share | modes | preview — a preview never marks a series recorded. */
  source: string;
  reached: number;
  revealed: number;
  realCount: number;
  fakeCount: number;
  total: number;
  finished: boolean;
  at: string;
  lastSeen: string;
}

export interface ActivityEntry {
  id: string;
  who: string;
  type: string;
  notes: string;
  at: string;
}

export type ItemPatch = Partial<
  Pick<RoundItem, 'headline' | 'verdict' | 'sourceName' | 'sourceUrl' | 'publishedAt' | 'artStatus' | 'body' | 'footerNote' | 'imageHint' | 'notes' | 'position'>
>;

/** The API speaks snake_case; the UI speaks camelCase. Convert in exactly one place. */
function toItemBody(patch: ItemPatch): Record<string, unknown> {
  const map: Record<string, string> = {
    headline: 'headline',
    verdict: 'verdict',
    sourceName: 'source_name',
    sourceUrl: 'source_url',
    publishedAt: 'published_at',
    artStatus: 'art_status',
    body: 'body',
    footerNote: 'footer_note',
    imageHint: 'image_hint',
    notes: 'notes',
    position: 'position',
  };
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(patch)) {
    const mapped = map[key];
    if (mapped !== undefined) out[mapped] = value;
  }
  return out;
}

// ── calls ───────────────────────────────────────────────────────────────────────────────────

export const api = {
  me: () => call<{ id: string; email: string; name: string; brand: string }>('/me'),

  listRounds: () => call<{ rounds: RoundSummary[] }>('/rounds').then((r) => r.rounds),

  getRound: (id: string) =>
    call<{ round: RoundDetail; problems: ReadinessProblem[]; spread: DecadeSpread }>(`/rounds/${id}`),

  createRound: (data: { title: string; targetSize?: number | null }) =>
    call<{ round: RoundSummary }>('/rounds', {
      method: 'POST',
      body: JSON.stringify({ title: data.title, target_size: data.targetSize ?? null }),
    }).then((r) => r.round),

  updateRound: (
    id: string,
    data: {
      title?: string; status?: RoundStatus; targetSize?: number | null; notes?: string;
      ribbonEnabled?: boolean; ribbonLabel?: string; headlineSize?: HeadlineSize; wordmarkEnabled?: boolean;
      playOrder?: PlayOrder;
    },
  ) =>
    call<{ round: RoundSummary }>(`/rounds/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        ...(data.title !== undefined ? { title: data.title } : {}),
        ...(data.status !== undefined ? { status: data.status } : {}),
        ...(data.targetSize !== undefined ? { target_size: data.targetSize } : {}),
        ...(data.notes !== undefined ? { notes: data.notes } : {}),
        ...(data.ribbonEnabled !== undefined ? { ribbon_enabled: data.ribbonEnabled } : {}),
        ...(data.ribbonLabel !== undefined ? { ribbon_label: data.ribbonLabel } : {}),
        ...(data.headlineSize !== undefined ? { headline_size: data.headlineSize } : {}),
        ...(data.wordmarkEnabled !== undefined ? { wordmark_enabled: data.wordmarkEnabled } : {}),
        ...(data.playOrder !== undefined ? { play_order: data.playOrder } : {}),
      }),
    }).then((r) => r.round),

  /* The endless pool, addressed by name. There is no id to pass because it is a singleton the
     server creates on first use — which also means these two never 404. */
  endless: () => call<{ round: RoundDetail; problems: ReadinessProblem[]; spread: DecadeSpread }>('/endless'),

  /* The Intake Queue. Same payload shape as a round, so the builder renders it with the editor
     it already has rather than a parallel screen that would drift. */
  /* The fake bank. Same shape as /endless and /intake so RoundEditor renders it with no new code.
     ⚠️ There is no `fakePlay` and there must not be: a run of nothing but fabricated headlines is
     not the game. It reaches Adorian merged into Endless Mode, or moved into a series. */
  fake: () => call<{ round: RoundDetail; problems: ReadinessProblem[]; spread: DecadeSpread }>('/fake'),
  intake: () => call<{ round: RoundDetail; problems: ReadinessProblem[]; spread: DecadeSpread; collectors: CollectorRuns | null }>('/intake'),

  /** Reject candidates: removes them AND remembers the URLs so the collector never re-offers them. */
  rejectIntake: (itemIds: string[], reason = '') =>
    call<{ rejectedCount: number; failedCount: number }>('/intake/reject', {
      method: 'POST',
      body: JSON.stringify({ itemIds, reason }),
    }),
  endlessPlay: () => call<{ round: PlayRound }>('/endless/play').then((r) => r.round),

  /* Reassign headlines to another round — "grab me ten out of Endless". A move, not a copy:
     what leaves Endless is gone from Endless. Pass either an existing `toRoundId` or a
     `newSeriesTitle` to create the destination on the spot.

     `movedCount` is the truth, not the length of what was sent: the batch is not atomic, so
     the caller reports what actually moved. */
  moveItems: (itemIds: string[], to: { toRoundId: string } | { newSeriesTitle: string }) =>
    call<{ movedCount: number; failedCount: number; created: boolean; round: RoundSummary }>(
      '/items/move',
      { method: 'POST', body: JSON.stringify({ itemIds, ...to }) },
    ),

  deleteRound: (id: string) => call<{ ok: true }>(`/rounds/${id}`, { method: 'DELETE' }),

  createItem: (roundId: string, patch: ItemPatch) =>
    call<{ item: RoundItem }>(`/rounds/${roundId}/items`, {
      method: 'POST',
      body: JSON.stringify(toItemBody(patch)),
    }).then((r) => r.item),

  /* `expectedUpdated` is the version this editor started from. If the row has moved on since,
     the server refuses with 409 rather than overwriting — Alyssa and Jason both work in here,
     and the second save used to win silently and completely. Omit it to write unconditionally,
     which is what "Keep mine" does once the person has actually seen the other version. */
  updateItem: (itemId: string, patch: ItemPatch, expectedUpdated?: string) =>
    call<{ item: RoundItem }>(`/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify({ ...toItemBody(patch), ...(expectedUpdated ? { expectedUpdated } : {}) }),
    }).then((r) => r.item),

  /* Hides the headline rather than destroying it — it lands in the round's trash and can be
     restored for 30 days, artwork and all. */
  deleteItem: (itemId: string) => call<{ ok: true }>(`/items/${itemId}`, { method: 'DELETE' }),

  /** What this round has deleted, newest first. Opening it also purges anything past its keep. */
  trash: (roundId: string) =>
    call<{ items: RoundItem[]; keptForDays: number }>(`/rounds/${roundId}/trash`),

  restoreItem: (itemId: string) =>
    call<{ item: RoundItem; restored: boolean }>(`/items/${itemId}/restore`, { method: 'POST' }),

  /** Move one item between two neighbours; the server writes a single position. */
  moveItem: (roundId: string, itemId: string, after?: number, before?: number) =>
    call<{ item?: RoundItem; items?: RoundItem[]; compacted: boolean }>(`/rounds/${roundId}/reorder`, {
      method: 'POST',
      body: JSON.stringify({ itemId, after, before }),
    }),

  bulkCreate: (roundId: string, items: ItemPatch[], replace = false) =>
    call<{ created: RoundItem[]; rejected: { row: number; reason: string }[] }>(`/rounds/${roundId}/bulk`, {
      method: 'POST',
      body: JSON.stringify({ items: items.map(toItemBody), replace }),
    }),

  /** Reads the headline off an article. Never throws on a site that refuses us — see below. */
  linkTitle: (url: string) =>
    call<{ ok: boolean; url?: string; title?: string; sourceName?: string; publishedAt?: string; reason?: string }>('/link-title', {
      method: 'POST',
      body: JSON.stringify({ url }),
    }),

  /* One batch of the Shoot-Ready check. Deliberately not a whole-round call: a Pages Function
     has a per-request budget, and a preflight that dies half-done looks like a pass. Call it
     until `remaining` comes back empty. */
  preflight: (roundId: string, itemIds?: string[]) =>
    call<{ results: PreflightResult[]; remaining: string[]; batchSize?: number }>(`/rounds/${roundId}/preflight`, {
      method: 'POST',
      body: JSON.stringify({ itemIds: itemIds ?? [] }),
    }),

  sessions: (roundId: string) =>
    call<{ sessions: PlaySession[] }>(`/rounds/${roundId}/session`).then((r) => r.sessions),

  activity: (roundId: string) =>
    call<{ activity: ActivityEntry[] }>(`/rounds/${roundId}/activity`).then((r) => r.activity),

  listLinks: (roundId: string) =>
    call<{ links: ShareLink[] }>(`/share?roundId=${encodeURIComponent(roundId)}`).then((r) => r.links),

  createLink: (roundId: string, label = '') =>
    call<{ link: ShareLink }>('/share', { method: 'POST', body: JSON.stringify({ roundId, label }) }).then((r) => r.link),

  revokeLink: (linkId: string) =>
    call<{ link: ShareLink }>(`/share/${linkId}`, { method: 'DELETE' }).then((r) => r.link),

  uploadItemImages: (itemId: string, files: File[], remove: string[] = []) => {
    const form = new FormData();
    files.forEach((f) => form.append('images', f, f.name));
    remove.forEach((name) => form.append('remove', name));
    return call<{ item: RoundItem }>(`/upload/item/${itemId}`, { method: 'POST', body: form }).then((r) => r.item);
  },

  uploadStamp: (roundId: string, which: 'real' | 'fake', file: File | null) => {
    const form = new FormData();
    form.append('which', which);
    if (file) form.append('image', file, file.name);
    else form.append('clear', '1');
    return call<{ round: RoundSummary }>(`/upload/round-stamp/${roundId}`, { method: 'POST', body: form }).then((r) => r.round);
  },

  /** The signed-in team reviewing their own round, without minting a public link. */
  preview: (roundId: string) =>
    call<{ round: PlayRound }>(`/rounds/${roundId}/preview`).then((r) => r.round),

  /** Public — no auth header needed, and none is sent for it by design. */
  play: async (token: string): Promise<PlayRound> => {
    const res = await fetch(`${BASE}/play/${encodeURIComponent(token)}`);
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new ApiCallError({
        error: body?.error || 'link_unavailable',
        message: body?.message || 'This play link is no longer active.',
        status: res.status,
      });
    }
    const body = await res.json();
    return body.round as PlayRound;
  },
};

/** The play payload uses the shared Round shape, with composed fake pages. */
export function playUrlFor(token: string): string {
  return `${window.location.origin}${import.meta.env.BASE_URL}play/${token}`;
}

/** Team-only preview of a whole round, or of one page within it. */
export function previewUrlFor(roundId: string, slug?: string): string {
  const base = `${window.location.origin}${import.meta.env.BASE_URL}rounds/${roundId}/preview`;
  return slug ? `${base}/${encodeURIComponent(slug)}` : base;
}

/* Endless has no preview route of its own: reviewing it IS playing it, exactly as Adorian
   will. Sending the team to the real surface rather than a lookalike is the point. */
/** The intake page. A singleton, so it is addressed by name rather than by id. */
export function intakeUrl(): string {
  return `${window.location.origin}${import.meta.env.BASE_URL}intake`;
}

export function endlessPlayUrl(): string {
  return `${window.location.origin}${import.meta.env.BASE_URL}modes/endless`;
}

/** Adorian's front door. The team opens it to see exactly what he sees. */
export function modesUrl(): string {
  return `${window.location.origin}${import.meta.env.BASE_URL}modes`;
}
